//Fix me: Delete this component after design changes as new component searchCustomerContainer has been created
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router';
import classNames from 'classnames';
import { useQuery } from '@apollo/client';
import { ShipToPartyDto } from '@atlassian/commerce-ccp-gateway-client';
import Modal, { ModalBody, ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import { SelectBillingProfilePageLoader } from '@css-xp/ppc-page-loaders';
import Button from '@atlaskit/button';
import { token } from '@atlaskit/tokens';
import { N500, N90 } from '@atlaskit/theme/colors';
import SearchIcon from '@atlaskit/icon/glyph/search';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import PersonIcon from '@atlaskit/icon/core/person';
import { getShipToParties } from '@atlassian/temp-proxies';
import Select, { components, InputProps, OptionType, ValueType } from '@atlaskit/select';
import {
  CommerceAddress,
  FetchCustomersForTransactionAccountQuery,
  FetchCustomersForTransactionAccountQueryVariables,
} from '@css-xp/graphtypes/src/generated-types-new';
import { errorNotificationBuilder, NotificationTypes, useNotification } from '@css-xp/base';
import { ADVOCATE_ACCESS_TYPE, ADVOCATE_RESOURCE_TYPE, AdvocateAccessWall } from '@css-xp/ppc-access-control';
import { CartCustomerSearchItem, CartCustomerSearchItemMap } from '../types';
import { fetchCustomers } from '../../../../queries/fetch-customers';
import './search-customer-modal.scss';
import styled from '@emotion/styled';

const StyledSelectedCustomer = styled.div`
  .ppc-customer-company-select__single-value {
    margin-left: ${token('space.300')};
  }
`;

const StyledBillingProfileLoader = styled.div`
  width: 100%;
`;

const Input = (props: InputProps) => {
  return (
    <div className="ppc-cutstomer-search-icon">
      <SearchIcon primaryColor={N90} label="search" size="small" />
      <components.Input {...props} />
    </div>
  );
};

const NoOptionsMessage = (props: any) => {
  return <components.NoOptionsMessage {...props}>No Results Found</components.NoOptionsMessage>;
};
/**
 * Mapping name of the customer with multiple ids associated with it
 * @param data
 */
const getCustomerList = (
  data?: FetchCustomersForTransactionAccountQuery,
  customerDetails?: CommerceAddress[] | null
): { name: string; ids: string[] }[] => {
  const shipToParties = customerDetails ?? data?.transactionAccount?.availableShipToParties?.nodes ?? [];
  const companyNameObj: CartCustomerSearchItemMap = {};
  shipToParties.forEach((node) => {
    if (node && node.name && node.id) {
      companyNameObj[node.name] = companyNameObj[node.name] || { name: node.name, ids: [] };
      companyNameObj[node.name].ids.push(node.id);
    }
  });

  return Object.values(companyNameObj);
};

const getCustomerListFromCCPResponse = (data: ShipToPartyDto[] | undefined): { name: string; ids: string[] }[] => {
  const companyNameObj: CartCustomerSearchItemMap = {};
  data?.forEach((node) => {
    if (node && node.name && node.id) {
      companyNameObj[node.name] = companyNameObj[node.name] || { name: node.name, ids: [] };
      companyNameObj[node.name].ids.push(node.id);
    }
  });

  return Object.values(companyNameObj);
};

export const SearchCustomerModal = ({
  isOpen,
  onSelectCustomer,
  onClose,
  onAddCustomerClick,
  isCreateCustomerEnabled,
  hideCustomerCloseModalButton = false,
  isFetchCustomerViaCCPEnabled = false,
  isNewSearchDesignEnabled = false,
  version,
  selectedCustomer,
  customerDetails,
}: {
  isOpen?: boolean;
  onSelectCustomer: (_item: CartCustomerSearchItem) => void;
  onClose?: () => void;
  onAddCustomerClick?: () => void;
  isCreateCustomerEnabled?: boolean;
  hideCustomerCloseModalButton?: boolean;
  isFetchCustomerViaCCPEnabled?: boolean;
  isNewSearchDesignEnabled?: boolean;
  version?: number | null;
  selectedCustomer?: CartCustomerSearchItem | null;
  customerDetails?: CommerceAddress[] | null;
}) => {
  const { txaId } = useParams<{ txaId: string }>();
  const allCustomersRef = useRef<CartCustomerSearchItem[]>([]);
  const { sendErrorNotification } = useNotification();
  const isVersion2 = version === 2;

  const { data, loading, error } = useQuery<
    FetchCustomersForTransactionAccountQuery,
    FetchCustomersForTransactionAccountQueryVariables
  >(fetchCustomers, {
    variables: {
      id: txaId,
      viewTaxRelatedInfo: false,
    },
    fetchPolicy: 'cache-and-network',
    skip: isFetchCustomerViaCCPEnabled || !txaId || !!customerDetails,
  });

  const [shipToParties, setShipToParties] = useState<{
    data: ShipToPartyDto[] | undefined;
    loading: boolean;
    error: string | Error;
  }>({
    data: undefined,
    loading: true,
    error: '',
  });

  const fetchShipToParties = useCallback(async () => {
    try {
      const res = await getShipToParties(txaId);
      setShipToParties({ data: res.data, loading: false, error: '' });
    } catch (err) {
      setShipToParties({ data: [], loading: false, error: err as any });
    }
  }, [txaId]);

  useEffect(() => {
    if (isFetchCustomerViaCCPEnabled) {
      fetchShipToParties();
    }
  }, [isFetchCustomerViaCCPEnabled, fetchShipToParties]);

  allCustomersRef.current = isFetchCustomerViaCCPEnabled
    ? getCustomerListFromCCPResponse(shipToParties.data)
    : getCustomerList(data, customerDetails);

  // Reset customers when changes on txa
  useEffect(() => {
    allCustomersRef.current = [];
  }, [txaId]);

  const customerSelectOptions = useMemo(() => {
    const sortedCustomerList = allCustomersRef.current?.sort((a, b) => a.name.localeCompare(b.name));
    return sortedCustomerList?.map(({ name }) => ({ label: name, value: name }));
  }, [data, shipToParties.data, customerDetails]);

  const handleChange = (selectedCustomer: ValueType<OptionType, false>) => {
    const selected = allCustomersRef.current.find(({ name }) => name === selectedCustomer?.value);
    if (selected) {
      onSelectCustomer(selected);
    }
  };

  useEffect(() => {
    if (error || shipToParties.error) {
      const notification = errorNotificationBuilder()
        .withHeader(`Not able to fetch the customer list`)
        .withMessage(`Please reload the page. If the issue persists, please contact us.`)
        .isVersion(NotificationTypes.FLAG)
        .build();
      sendErrorNotification(notification);
    }
  }, [error, shipToParties.error]);

  const renderSelectComponent = () => {
    return loading ? (
      <StyledBillingProfileLoader>
        <SelectBillingProfilePageLoader />
      </StyledBillingProfileLoader>
    ) : (
      <StyledSelectedCustomer>
        <Select
          autoFocus
          classNamePrefix={'ppc-customer-company-select'}
          components={{
            Input: Input,
            NoOptionsMessage: NoOptionsMessage,
            DropdownIndicator: null,
          }}
          placeholder="Enter customer company name"
          isLoading={isFetchCustomerViaCCPEnabled ? shipToParties.loading : loading}
          options={customerSelectOptions}
          isSearchable={true}
          closeMenuOnSelect={true}
          onChange={handleChange}
          id={`customer-company-select`}
          {...(isVersion2 && {
            value: selectedCustomer ? { label: selectedCustomer.name, value: selectedCustomer.name } : null,
          })}
        />
      </StyledSelectedCustomer>
    );
  };

  const renderAddNewCustomerComponent = () => {
    return (
      isCreateCustomerEnabled && (
        <AdvocateAccessWall accessType={ADVOCATE_ACCESS_TYPE.CREATE} resource={ADVOCATE_RESOURCE_TYPE.CUSTOMER}>
          <Button
            testId="create-customer-button"
            appearance={isNewSearchDesignEnabled ? 'primary' : 'link'}
            onClick={onAddCustomerClick}
            iconBefore={isNewSearchDesignEnabled ? <PersonIcon label="add-customer-icon" /> : undefined}
            className="create-customer-button"
          >
            {isNewSearchDesignEnabled ? 'New customer' : '+ New customer'}
          </Button>
        </AdvocateAccessWall>
      )
    );
  };

  if (isVersion2) {
    return (
      <div
        className={classNames('search-customer-modal', {
          'search-customer-modal-v2': isVersion2,
        })}
      >
        {renderSelectComponent()}
        {renderAddNewCustomerComponent()}
      </div>
    );
  } else {
    return (
      <ModalTransition>
        {isOpen && (
          <Modal testId="search-customer-modal" height={500}>
            <ModalHeader>
              <ModalTitle>Firstly, who's this for?</ModalTitle>
              {!hideCustomerCloseModalButton && (
                <Button appearance="link" onClick={onClose} testId="search-customer-close-modal-button">
                  <CrossIcon size="small" label="Close Modal" primaryColor={token('color.text.subtle', N500)} />
                </Button>
              )}
            </ModalHeader>
            <ModalBody>
              <div className={isNewSearchDesignEnabled ? 'search-customer-modal' : ''}>
                {renderSelectComponent()}
                {renderAddNewCustomerComponent()}
              </div>
            </ModalBody>
          </Modal>
        )}
      </ModalTransition>
    );
  }
};
